import React, { useEffect, useState } from "react";
import { PageContainer, PageHeader, Card, ConfirmDelete } from "./Shared";
import { adminApi } from "./api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import RichTextEditor from "./RichTextEditor";
import ImageUpload from "./ImageUpload";
import { runValidators, validators } from "./validators";

const empty = { title: "", excerpt: "", body: "", category: "Insights", cover_image_id: null, status: "published", slug: "" };

export default function BlogManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, object = edit
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await adminApi.get("/admin/blog");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.body) { toast.error("Title and body are required"); return; }
    const slugErr = runValidators(form.slug || "", [validators.slug]);
    if (slugErr) { toast.error(`Slug: ${slugErr}`); return; }
    setBusy(true);
    try {
      if (editing) await adminApi.put(`/admin/blog/${editing.id}`, form);
      else await adminApi.post("/admin/blog", form);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    await adminApi.delete(`/admin/blog/${id}`);
    toast.success("Deleted");
    await load();
  };

  return (
    <PageContainer>
      <PageHeader
        title="Blog Posts"
        subtitle="Write and publish articles for the Resources section."
        action={<Button onClick={openCreate} data-testid="blog-new" className="text-white" style={{ background: "#1B6FE0" }}><Plus size={14} weight="bold" /> New post</Button>}
      />

      <Card>
        <ul className="divide-y divide-slate-100" data-testid="blog-list">
          {items.length === 0 && <li className="p-8 text-center text-[#6B7A8D]">No posts yet.</li>}
          {items.map((it) => (
            <li key={it.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`blog-item-${it.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[14.5px]" style={{ color: "#0F2A6E" }}>{it.title}</div>
                <div className="text-[12px] text-[#6B7A8D] mt-0.5">/{it.slug} · {it.category} · <span className={it.status === "published" ? "text-emerald-600" : "text-amber-600"}>{it.status}</span></div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => openEdit(it)} data-testid={`blog-edit-${it.id}`}><PencilSimple size={13} weight="bold" /> Edit</Button>
                <ConfirmDelete title="Delete this post?" description={`"${it.title}" will be permanently removed.`} onConfirm={() => del(it.id)} testId={`blog-del-${it.id}`}>
                  <Button variant="outline" size="sm" data-testid={`blog-del-${it.id}`} className="text-[#1B6FE0] hover:bg-blue-50 hover:text-[#1B6FE0]"><Trash size={13} weight="bold" /></Button>
                </ConfirmDelete>
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="blog-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit post" : "New post"}</DialogTitle>
            <DialogDescription>Write a blog post. Slug is auto-generated from title if left empty.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label htmlFor="b-title">Title</Label>
                <Input id="b-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" required data-testid="blog-title" />
              </div>
              <div>
                <Label htmlFor="b-cat">Category</Label>
                <Input id="b-cat" value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1.5" data-testid="blog-category" />
              </div>
              <div className="md:col-span-3">
                <Label htmlFor="b-slug">Slug (optional · lowercase letters, digits and dashes)</Label>
                <Input id="b-slug" value={form.slug || ""} onChange={(e) => set("slug", e.target.value.toLowerCase())} className="mt-1.5" placeholder="my-post-slug" data-testid="blog-slug" />
              </div>
            </div>
            <div>
              <Label htmlFor="b-ex">Excerpt</Label>
              <Textarea id="b-ex" rows={2} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} className="mt-1.5" data-testid="blog-excerpt" />
            </div>
            <ImageUpload value={form.cover_image_id} onChange={(id) => set("cover_image_id", id)} label="Cover image" testId="blog-cover" />
            <div>
              <Label>Body</Label>
              <div className="mt-1.5">
                <RichTextEditor value={form.body} onChange={(html) => set("body", html)} />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="w-[160px]" data-testid="blog-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ background: "#1B6FE0" }} data-testid="blog-save">
                {busy ? "Saving…" : (editing ? "Update" : "Create")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
